import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionLabel } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const experienceOptions = [
  "Complete beginner",
  "Less than 1 year",
  "1 to 3 years",
  "3+ years",
  "Funded / professional",
];

const availabilityOptions = [
  "Under 5 hrs / week",
  "5 to 10 hrs / week",
  "10 to 20 hrs / week",
  "20+ hrs / week",
  "Full time focus",
];

const STEPS = [
  { key: "you", label: "You", fields: ["name", "email", "phone"] },
  { key: "experience", label: "Experience", fields: ["experience", "goals"] },
  { key: "commitment", label: "Commitment", fields: ["availability", "motivation"] },
];

const empty = {
  name: "",
  email: "",
  phone: "",
  experience: "",
  goals: "",
  availability: "",
  motivation: "",
};

export default function FinalCTA() {
  const [form, setForm] = useState(empty);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const stepValid = () => {
    if (step === 0) return form.name.trim() && emailValid && form.phone.trim().length >= 3;
    if (step === 1) return form.experience && form.goals.trim();
    return form.availability && form.motivation.trim();
  };

  const next = () => {
    if (!stepValid()) {
      toast.error("Please complete this step to continue.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!stepValid()) {
      toast.error("Please complete this step.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, form);
      setDone(true);
      toast.success("Application received.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm(empty);
    setStep(0);
    setDone(false);
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 focus:border-[#6ea8ff]/70 text-white text-base font-light py-3 outline-none transition-colors duration-300 placeholder:text-zinc-600";

  return (
    <section
      id="apply"
      className="relative py-24 sm:py-32 lg:py-40 border-t border-white/5 overflow-hidden"
      data-testid="apply-section"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.15]"
        style={{
          background:
            "radial-gradient(circle at 50% 40%, rgba(110,168,255,0.1) 0%, rgba(10,10,10,0) 55%)",
        }}
      />

      <div className="relative max-w-2xl mx-auto px-6 lg:px-8">
        <Reveal>
          <div className="text-center">
            <SectionLabel>Apply for Mentorship</SectionLabel>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter">
              Your edge begins <span className="silver-text">in the mind.</span>
            </h2>
            <p className="mt-6 text-base lg:text-lg text-zinc-400 font-light max-w-xl mx-auto">
              A considered, by application process. Every submission is reviewed
              personally, this is not a checkout.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          {done ? (
            <div className="mt-16 border border-white/15 bg-[#0F0F0F] p-10 sm:p-14 text-center" data-testid="apply-success">
              <div className="mx-auto h-14 w-14 rounded-full border border-[#6ea8ff]/40 flex items-center justify-center">
                <Check className="h-6 w-6 text-[#6ea8ff]" strokeWidth={1.5} />
              </div>
              <div className="mt-8 font-heading text-2xl sm:text-3xl font-light silver-text">
                Application received, {form.name.split(" ")[0] || "trader"}.
              </div>
              <p className="mt-4 text-sm sm:text-base text-zinc-400 font-light max-w-md mx-auto leading-relaxed">
                Thank you for applying to MBM. You've entered our review pipeline —
                we assess every application personally and will reach out by email
                to <span className="text-zinc-200">{form.email}</span> with next steps.
              </p>
              <button
                onClick={reset}
                className="mt-10 section-label text-[10px] text-zinc-500 hover:text-white transition-colors"
                data-testid="apply-reset-btn"
              >
                Submit another application
              </button>
            </div>
          ) : (
            <div className="mt-14" data-testid="apply-form">
              {/* progress */}
              <div className="flex items-center gap-3 mb-12">
                {STEPS.map((s, i) => (
                  <div key={s.key} className="flex-1">
                    <div
                      className="h-px w-full transition-colors duration-500"
                      style={{ background: i <= step ? "#6ea8ff" : "rgba(255,255,255,0.15)" }}
                    />
                    <div className="mt-3 flex items-center gap-2">
                      <span
                        className="section-label text-[9px]"
                        style={{ color: i <= step ? "#9fd3ff" : "#52525b" }}
                      >
                        0{i + 1}
                      </span>
                      <span
                        className="section-label text-[9px]"
                        style={{ color: i === step ? "#e4e4e7" : "#52525b" }}
                      >
                        {s.label}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="space-y-10 min-h-[240px]"
                >
                  {step === 0 && (
                    <>
                      <Field label="Full Name">
                        <input className={inputCls} value={form.name} onChange={update("name")} placeholder="Your name" data-testid="apply-name" />
                      </Field>
                      <Field label="Email">
                        <input type="email" className={inputCls} value={form.email} onChange={update("email")} placeholder="you@email.com" data-testid="apply-email" />
                      </Field>
                      <Field label="Phone">
                        <input type="tel" className={inputCls} value={form.phone} onChange={update("phone")} placeholder="+61 4XX XXX XXX" data-testid="apply-phone" />
                      </Field>
                    </>
                  )}

                  {step === 1 && (
                    <>
                      <Field label="Trading Experience">
                        <select className={`${inputCls} appearance-none cursor-pointer [&>option]:bg-[#0F0F0F] [&>option]:text-white`} value={form.experience} onChange={update("experience")} data-testid="apply-experience">
                          <option value="" disabled>Select your experience</option>
                          {experienceOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </Field>
                      <Field label="Your Goals, what does success look like?">
                        <textarea rows={3} className={`${inputCls} resize-none`} value={form.goals} onChange={update("goals")} placeholder="e.g. consistent process, pass a funded evaluation, master my psychology…" data-testid="apply-goals" />
                      </Field>
                    </>
                  )}

                  {step === 2 && (
                    <>
                      <Field label="Availability">
                        <select className={`${inputCls} appearance-none cursor-pointer [&>option]:bg-[#0F0F0F] [&>option]:text-white`} value={form.availability} onChange={update("availability")} data-testid="apply-availability">
                          <option value="" disabled>How much time can you commit?</option>
                          {availabilityOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </Field>
                      <Field label="Motivation, why MBM, and why now?">
                        <textarea rows={4} className={`${inputCls} resize-none`} value={form.motivation} onChange={update("motivation")} placeholder="Tell us what you want to master." data-testid="apply-motivation" />
                      </Field>
                    </>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* nav */}
              <div className="mt-12 flex items-center justify-between gap-4">
                <button
                  onClick={back}
                  disabled={step === 0}
                  className="flex items-center gap-2 section-label text-[10px] text-zinc-400 hover:text-white transition-colors disabled:opacity-0"
                  data-testid="apply-back"
                >
                  <ArrowLeft size={14} /> Back
                </button>

                {step < STEPS.length - 1 ? (
                  <button
                    onClick={next}
                    className="btn-solid flex items-center gap-2 px-8 py-3.5 text-sm font-medium tracking-wide"
                    data-testid="apply-next"
                  >
                    Continue <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    onClick={submit}
                    disabled={loading}
                    className="btn-solid px-8 py-3.5 text-sm font-medium tracking-wide disabled:opacity-50"
                    data-testid="apply-submit"
                  >
                    {loading ? "Submitting…" : "Submit Application"}
                  </button>
                )}
              </div>
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="section-label text-[10px] text-zinc-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);
